import React, { memo, useMemo } from "react";
import { Truck, AlertOctagon } from "lucide-react";
import { StatusDot } from "../layout/StatsCard";
import { getOutOfServiceDuration } from "../../utils/helpers";

export const VehicleRow = memo(({ vehicle, onClick, serviceIntervals = [] }) => {
  // Memoize expensive calculations
  const { bitPastDue, dotPastDue, oilPastDue } = useMemo(() => {
    // Calculate if service is past due based on intervals
    const isServicePastDue = (lastServiceDate, lastServiceMileage, intervalName) => {
      if (!lastServiceDate) return true;
      
      const interval = serviceIntervals.find(i => 
        i.name.toLowerCase().includes(intervalName.toLowerCase()) ||
        intervalName.toLowerCase().includes(i.name.toLowerCase().split(' ')[0])
      );
      
      if (!interval) return !lastServiceDate;
      
      const lastDate = new Date(lastServiceDate);
      const now = new Date();
      const daysSinceService = Math.floor((now - lastDate) / (1000 * 60 * 60 * 24));
      const milesSinceService = vehicle.mileage - (lastServiceMileage || 0);
      
      switch (interval.interval_unit) {
        case 'days':
          return daysSinceService > interval.interval_value;
        case 'months':
          return daysSinceService > (interval.interval_value * 30);
        case 'yearly':
          return daysSinceService > (interval.interval_value * 365);
        case 'miles':
          return milesSinceService > interval.interval_value;
        case 'hours':
          return false;
        default:
          return false;
      }
    };

    // CNG oil change: 1,000 hours instead of 10,000 miles
    const isCngOilPastDue = () => {
      if (vehicle.fuel_type !== 'cng') return false;
      if (!vehicle.last_oil_change) return true;
      const currentHours = vehicle.engine_hours || 0;
      const lastHours = vehicle.last_oil_hours || 0;
      const hoursSinceService = currentHours - lastHours;
      return hoursSinceService > 1000;
    };

    return {
      bitPastDue: isServicePastDue(vehicle.last_bit_inspection, vehicle.last_bit_mileage, "B.I.T"),
      dotPastDue: isServicePastDue(vehicle.last_dot_inspection, vehicle.last_dot_mileage, "D.O.T"),
      oilPastDue: vehicle.fuel_type === 'cng' 
        ? isCngOilPastDue() 
        : isServicePastDue(vehicle.last_oil_change, vehicle.last_oil_mileage, "Oil")
    };
  }, [vehicle, serviceIntervals]);
  
  const fuelLabel = vehicle.fuel_type === "diesel" ? "DSL" : "CNG";
  const transLabel = vehicle.transmission_type === "automatic" ? "AUTO" : vehicle.transmission_type === "standard" ? "STD" : null;
  const oosDuration = vehicle.is_out_of_service ? getOutOfServiceDuration(vehicle.out_of_service_date) : null;
  
  return (
    <tr 
      className={`vehicle-row cursor-pointer ${vehicle.is_out_of_service ? 'out-of-service-row' : ''}`}
      onClick={onClick}
      data-testid={`vehicle-row-${vehicle.id}`}
    >
      <td>
        <div className="flex items-center gap-3">
          <div className={`vehicle-icon-wrapper ${vehicle.is_out_of_service ? 'bg-red-50' : 'bg-blue-50'}`}>
            <Truck className={`w-5 h-5 ${vehicle.is_out_of_service ? 'text-red-500' : 'text-blue-600'}`} />
          </div>
          <div className="vehicle-info">
            <div className="vehicle-header">
              <span className="vehicle-unit">{vehicle.unit_number}</span>
              <span className={`type-badge ${vehicle.vehicle_type}`}>
                {vehicle.vehicle_type.replace('_', ' ')}
              </span>
              <span className={`fuel-badge ${vehicle.fuel_type}`}>{fuelLabel}</span>
              {vehicle.def_equipped && <span className="fuel-badge def">DEF</span>}
              {transLabel && <span className={`trans-badge ${vehicle.transmission_type}`}>{transLabel}</span>}
            </div>
            {vehicle.is_out_of_service && (
              <div className="oos-badge-row">
                <span className="out-of-service-badge" data-testid={`oos-badge-${vehicle.id}`}>
                  <AlertOctagon className="w-3 h-3" />
                  OUT OF SERVICE
                  {oosDuration && <span className="oos-duration">({oosDuration})</span>}
                </span>
              </div>
            )}
            <div className="vehicle-details">
              {vehicle.year} {vehicle.make} {vehicle.model}
            </div>
          </div>
        </div>
      </td>
      <td className="vehicle-vin-cell">
        <div className="text-sm text-slate-600">
          VIN: ...{vehicle.vin.slice(-8)}
        </div>
      </td>
      <td className="vehicle-mileage-cell">
        <div className="text-right">
          {vehicle.fuel_type === 'cng' ? (
            <>
              <div className="font-medium text-slate-900">
                {(vehicle.engine_hours || 0).toLocaleString()} <span className="text-xs font-normal text-slate-500">hrs</span>
              </div>
              <div className="text-xs text-slate-500">
                {vehicle.mileage.toLocaleString()} mi
              </div>
            </>
          ) : (
            <>
              <div className="font-medium text-slate-900">
                {vehicle.mileage.toLocaleString()}
              </div>
              <div className="text-xs text-slate-500">mi</div>
            </>
          )}
        </div>
      </td>
      <td className="vehicle-status-cell">
        <div className="status-dots-row">
          <StatusDot status={bitPastDue ? "due" : "ok"} label="BIT" />
          <StatusDot status={dotPastDue ? "due" : "ok"} label="DOT" />
          <StatusDot status={oilPastDue ? "due" : "ok"} label="OIL" />
        </div>
      </td>
    </tr>
  );
});

VehicleRow.displayName = 'VehicleRow';
